// How Golden Nominal maintenance events read in the Events list: review-queue decisions on a
// record, golden records created from duplicates, and the tenant's maintenance switch.
import { reviewReasonCodeLabel, reviewReasonLabel, reviewTypeLabel } from "common/reviewQueue";

const ACTION_LABELS = {
    REVIEW_QUEUED: "Sent to review",
    REVIEW_RESOLVED: "Review resolved",
    REVIEW_DISMISSED: "Review dismissed",
    REVIEW_SUPERSEDED: "Review closed",
    BIRTHED_GOLDEN_RECORD: "Golden record created",
    // Sent for the survivor, which took in its duplicates' values.
    MERGED_DUPLICATE_RECORD: "Duplicates merged in",
    // PRE-2837: a duplicate in its survivor's dataset flagged as subsumed, or restored (un-merged).
    SUBSUMED_DUPLICATE_RECORD: "Flagged as subsumed",
    RESTORED_DUPLICATE_RECORD: "Restored (un-merged)",
};

// What a subsumed record was restored from.
const RESTORED_FROM_LABELS = { SUBSUMED: "was flagged as subsumed", RETIRED: "was retired" };

const REVIEW_EVENT_TYPES = ["REVIEW_QUEUED", "REVIEW_RESOLVED", "REVIEW_DISMISSED", "REVIEW_SUPERSEDED"];

// Why a review was closed without a decision on it.
const RESOLUTION_LABELS = {
    SEARCH_RERUN: "search re-run",
    SEARCH_UNDONE: "search undone",
    STEWARD_RESOLVED: "resolved by a steward",
    STEWARD_DISMISSED: "dismissed by a steward",
    MERGED: "merged by a later run",
    DUPLICATES_DELETED: "duplicates deleted by a later run",
    DUPLICATES_KEPT: "processed by a later run, duplicates kept",
    BORN: "golden record created by a later run",
};

// A resolution a steward's action led to, when it reads differently from a run's.
const STEWARD_RESOLUTION_LABELS = {
    MERGED: "merged by a steward",
};

// The Action column; null for any other event.
export function goldenEventAction(event) {
    if (!event) {
        return null;
    }
    if (event.eventType === "GOLDEN_MAINTENANCE_SWITCHED") {
        return event.details && event.details.goldenMaintenance ? "Golden Nominal maintenance turned on" : "Golden Nominal maintenance turned off";
    }
    return ACTION_LABELS[event.eventType] || null;
}

// The Details column, as plain text; null for any other event.
export function goldenEventDetails(event) {
    const details = (event && event.details) || {};
    if (REVIEW_EVENT_TYPES.includes(event && event.eventType)) {
        const parts = [reviewTypeLabel(details.reviewType)];
        if (details.role === "PROPOSED_SURVIVOR") {
            parts.push("proposed survivor");
        }
        if (event.eventType === "REVIEW_QUEUED" && details.reason) {
            parts.push(reviewReasonLabel(details.reason));
        }
        // Events carry codes only, never a steward's own words.
        if (event.eventType !== "REVIEW_QUEUED" && details.resolution) {
            const stewardLabel = details.reasonCode && STEWARD_RESOLUTION_LABELS[details.resolution];
            parts.push(stewardLabel || RESOLUTION_LABELS[details.resolution] || details.resolution);
        }
        // A steward's reason code (their note is never in an event).
        if (details.reasonCode) {
            parts.push(reviewReasonCodeLabel(details.reasonCode));
        }
        return parts.filter(Boolean).join(" · ");
    }
    if (event && event.eventType === "GOLDEN_MAINTENANCE_SWITCHED") {
        return `${details.goldenMaintenance ? "On" : "Off"} (was ${details.previous ? "on" : "off"})`;
    }
    if (event && (event.eventType === "SUBSUMED_DUPLICATE_RECORD" || event.eventType === "RESTORED_DUPLICATE_RECORD")) {
        const parts = [
            event.eventType === "RESTORED_DUPLICATE_RECORD" ? RESTORED_FROM_LABELS[details.restoredFrom] : null,
            details.survivedRecordId ? `survivor ${details.survivedRecordId}` : null,
            details.reasonCode ? reviewReasonCodeLabel(details.reasonCode) : null,
        ];
        return parts.filter(Boolean).join(" · ");
    }
    return null;
}

// The Record column for record types the list does not otherwise name.
export function goldenRecordTypeLabel(recordType) {
    return recordType === "TENANT" ? "Tenant" : null;
}
